import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, CheckCircle2, XCircle, Clock, Building2, Check, X as XIcon, CheckSquare, Square } from 'lucide-react';
import { adminApi } from '../../lib/api';
import { ALL_MODULES, getModulesForClientType } from '../../App';

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
    invited: { label: 'Invitation envoyée', className: 'bg-slate-500/10 text-slate-400' },
    submitted: { label: 'En attente de traitement', className: 'bg-warning-500/10 text-warning-500' },
    accepted: { label: 'Acceptée', className: 'bg-green-500/10 text-green-500' },
    refused: { label: 'Refusée', className: 'bg-red-500/10 text-red-500' },
};

const RegistrationRequestCard: React.FC<{ row: any; onResolved: () => void }> = ({ row, onResolved }) => {
    const [action, setAction] = React.useState<'accept' | 'refuse' | null>(null);
    const [selectedModules, setSelectedModules] = React.useState<string[]>([]);
    const [reason, setReason] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');
    const [resent, setResent] = React.useState(false);

    const availableModules = getModulesForClientType(row.client_type);

    const handleResend = async () => {
        setSaving(true);
        setError('');
        try {
            const result: any = await adminApi.resendRegistrationInvite(row.id);
            if (result?.emailSent === false) setError(result.emailError || "L'email n'a pas pu être renvoyé.");
            else { setResent(true); setTimeout(() => setResent(false), 3000); }
        } catch (e: any) {
            setError(e?.message || "Erreur lors du renvoi de l'invitation");
        } finally {
            setSaving(false);
        }
    };

    const startAccept = () => {
        // Vide par défaut — pas "tout coché" — pour qu'un clic sur un module veuille toujours dire
        // "je l'active" plutôt que de risquer de le désactiver sans s'en rendre compte (piège rencontré :
        // partir de "tout sélectionné" fait que cliquer dessus les désélectionne un par un).
        setSelectedModules([]);
        setAction('accept');
        setError('');
    };

    const toggleModule = (name: string) => {
        setSelectedModules((prev) => prev.includes(name) ? prev.filter((m) => m !== name) : [...prev, name]);
    };

    const handleAccept = async () => {
        setSaving(true);
        setError('');
        try {
            await adminApi.acceptRegistrationRequest(row.id, selectedModules);
            onResolved();
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'acceptation");
        } finally {
            setSaving(false);
        }
    };

    const handleRefuse = async () => {
        setSaving(true);
        setError('');
        try {
            await adminApi.refuseRegistrationRequest(row.id, reason);
            onResolved();
        } catch (e: any) {
            setError(e?.message || 'Erreur lors du refus');
        } finally {
            setSaving(false);
        }
    };

    const statusInfo = STATUS_LABELS[row.status] || { label: row.status, className: 'bg-slate-500/10 text-slate-400' };

    return (
        <div className="bg-surface border border-border rounded-2xl p-6 space-y-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-surface-soft border border-border flex items-center justify-center overflow-hidden shrink-0">
                        {row.logo_url ? <img src={row.logo_url} alt="" className="w-full h-full object-cover" /> : <Building2 size={18} className="text-content-muted" />}
                    </div>
                    <div>
                        <p className="font-bold text-content">{row.company_name || row.invite_email}</p>
                        <p className="text-xs text-content-muted">{row.full_name} · {row.contact_email || row.invite_email}</p>
                    </div>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-xs font-bold capitalize ${statusInfo.className}`}>{statusInfo.label}</span>
            </div>

            {row.status === 'invited' && (
                <div className="space-y-2 pt-2 border-t border-border">
                    {error && <div className="bg-red-500/10 border border-red-500/30 text-red-500 text-sm rounded-lg px-3 py-2">{error}</div>}
                    <button onClick={handleResend} disabled={saving}
                        className="px-4 py-2 rounded-lg border border-border text-content font-bold text-sm hover:bg-surface-soft disabled:opacity-50">
                        {saving ? 'Envoi...' : resent ? 'Invitation renvoyée ✓' : "Renvoyer l'invitation"}
                    </button>
                </div>
            )}

            {row.status === 'submitted' && (
                <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                        <div><p className="text-content-muted uppercase font-bold mb-0.5">Type</p><p className="text-content capitalize">{row.client_type}</p></div>
                        <div><p className="text-content-muted uppercase font-bold mb-0.5">Identifiant</p><p className="text-content">{row.username}</p></div>
                        <div><p className="text-content-muted uppercase font-bold mb-0.5">TVA</p><p className="text-content">{(row.tva_rates || []).map((r: number) => `${r}%`).join(', ')}</p></div>
                        <div><p className="text-content-muted uppercase font-bold mb-0.5">SIRET</p><p className="text-content">{row.siret || '—'}</p></div>
                        <div><p className="text-content-muted uppercase font-bold mb-0.5">TVA intraco.</p><p className="text-content">{row.tva || '—'}</p></div>
                        <div><p className="text-content-muted uppercase font-bold mb-0.5">Téléphone</p><p className="text-content">{row.phone || '—'}</p></div>
                        <div className="col-span-2 sm:col-span-3"><p className="text-content-muted uppercase font-bold mb-0.5">Adresse</p><p className="text-content">{[row.address, row.postal_code, row.city, row.country].filter(Boolean).join(', ') || '—'}</p></div>
                    </div>

                    {error && <div className="bg-red-500/10 border border-red-500/30 text-red-500 text-sm rounded-lg px-3 py-2">{error}</div>}

                    {action === 'accept' ? (
                        <div className="space-y-3 pt-2 border-t border-border">
                            <div className="flex items-center justify-between">
                                <p className="text-xs font-bold text-content-muted uppercase">Modules à activer ({selectedModules.length}/{availableModules.length})</p>
                                <button type="button" onClick={() => setSelectedModules(selectedModules.length === availableModules.length ? [] : availableModules.map((m) => m.name))}
                                    className="text-xs font-bold text-brand-600 dark:text-brand-400 hover:underline">
                                    {selectedModules.length === availableModules.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {availableModules.map((m) => {
                                    const isSelected = selectedModules.includes(m.name);
                                    return (
                                        <button key={m.name} type="button" onClick={() => toggleModule(m.name)}
                                            className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all inline-flex items-center gap-1.5 ${isSelected ? 'border-brand-500 bg-brand-500/10 text-brand-500' : 'border-border bg-surface-soft text-content-muted'}`}>
                                            {isSelected ? <CheckSquare size={13} /> : <Square size={13} />}
                                            {m.label}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => setAction(null)} className="flex-1 py-2 rounded-lg border border-border text-content font-bold hover:bg-surface-soft text-sm">Annuler</button>
                                <button onClick={handleAccept} disabled={saving} className="flex-1 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm disabled:opacity-50">
                                    {saving ? 'Création...' : 'Terminer'}
                                </button>
                            </div>
                        </div>
                    ) : action === 'refuse' ? (
                        <div className="space-y-3 pt-2 border-t border-border">
                            <p className="text-xs font-bold text-content-muted uppercase">Motif du refus</p>
                            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                                className="w-full bg-surface-soft border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500 resize-none"
                                placeholder="Expliquez brièvement la raison du refus (envoyé au demandeur)" />
                            <div className="flex gap-2">
                                <button onClick={() => setAction(null)} className="flex-1 py-2 rounded-lg border border-border text-content font-bold hover:bg-surface-soft text-sm">Annuler</button>
                                <button onClick={handleRefuse} disabled={saving} className="flex-1 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-bold text-sm disabled:opacity-50">
                                    {saving ? 'Envoi...' : 'Terminer'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex gap-2 pt-2 border-t border-border">
                            <button onClick={() => { setAction('refuse'); setError(''); }} className="flex-1 py-2 rounded-lg border border-border text-content font-bold hover:bg-red-500/10 hover:border-red-500/40 hover:text-red-500 text-sm inline-flex items-center justify-center gap-1.5">
                                <XIcon size={14} /> Refuser
                            </button>
                            <button onClick={startAccept} className="flex-1 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm inline-flex items-center justify-center gap-1.5">
                                <Check size={14} /> Accepter
                            </button>
                        </div>
                    )}
                </>
            )}

            {row.status === 'accepted' && (
                <div className="pt-2 border-t border-border">
                    <p className="text-xs text-content-muted uppercase font-bold mb-1.5">Modules activés</p>
                    <div className="flex flex-wrap gap-1.5">
                        {(row.granted_modules || []).length === 0 ? (
                            <span className="text-xs text-content-muted italic">Aucun module</span>
                        ) : (
                            (row.granted_modules || []).map((m: string) => (
                                <span key={m} className="px-2.5 py-1 rounded-lg bg-surface-soft border border-border text-xs font-bold text-content">
                                    {ALL_MODULES.find((am) => am.name === m)?.label || m}
                                </span>
                            ))
                        )}
                    </div>
                </div>
            )}

            {row.status === 'refused' && row.refusal_reason && (
                <p className="text-xs text-content-muted italic pt-2 border-t border-border">Motif : {row.refusal_reason}</p>
            )}
        </div>
    );
};

export const RegistrationRequestsView: React.FC = () => {
    const navigate = useNavigate();
    const [rows, setRows] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(true);

    const load = React.useCallback(() => {
        setLoading(true);
        adminApi.getRegistrationRequests().then((data: any) => setRows(Array.isArray(data) ? data : [])).catch((e) => console.error(e)).finally(() => setLoading(false));
    }, []);

    React.useEffect(() => { load(); }, [load]);

    const pending = rows.filter((r) => r.status === 'submitted');
    const others = rows.filter((r) => r.status !== 'submitted');

    return (
        <div className="p-8 max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
                <button onClick={() => navigate('/admin/clients')} className="p-2 rounded-lg border border-border text-content-muted hover:text-content hover:bg-surface-soft"><ArrowLeft size={18} /></button>
                <h1 className="text-2xl font-bold text-content">Demandes d'inscription</h1>
            </div>

            {loading ? (
                <div className="flex justify-center py-20"><Loader2 className="animate-spin text-content-muted" size={28} /></div>
            ) : rows.length === 0 ? (
                <div className="bg-surface rounded-2xl border border-border p-16 flex flex-col items-center justify-center text-center">
                    <Clock size={32} className="text-content-muted mb-3" />
                    <p className="text-content-muted">Aucune demande d'inscription pour le moment.</p>
                </div>
            ) : (
                <div className="space-y-8">
                    {pending.length > 0 && (
                        <div className="space-y-4">
                            <h2 className="text-sm font-bold text-content-muted uppercase tracking-widest">En attente ({pending.length})</h2>
                            {pending.map((row) => <RegistrationRequestCard key={row.id} row={row} onResolved={load} />)}
                        </div>
                    )}
                    {others.length > 0 && (
                        <div className="space-y-4">
                            <h2 className="text-sm font-bold text-content-muted uppercase tracking-widest">Historique ({others.length})</h2>
                            {others.map((row) => <RegistrationRequestCard key={row.id} row={row} onResolved={load} />)}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
